import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { baseUrl } from '../lib/api.js';
import { errorMessage } from '../lib/properties.js';

/**
 * `/interni/ai-superadmin` (krok KR1 bod 6) — zustatky vsech kancelari, rucni
 * bonus, kurz USD/CZK. ROZHODNUTI KR1-2: REOS zatim nema cross-tenant
 * superadmin roli, takze stranka se autorizuje sdilenym tokenem (hlavicka
 * `x-superadmin-token`, env `SUPERADMIN_TOKEN` na serveru) zadanym rucne do
 * teto stranky - NIKDY neuklada do localStorage/sessionStorage (CLAUDE.md §S3
 * - jen React state, prezije obnoveni stranky proto NE).
 */

interface BalanceRow {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly balanceCzk: string;
}

async function adminFetch<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), 'x-superadmin-token': token },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export function AiSuperadminPage(): React.JSX.Element {
  const t2 = useT();
  const [token, setToken] = React.useState('');
  const [activeToken, setActiveToken] = React.useState<string | null>(null);
  const [balances, setBalances] = React.useState<readonly BalanceRow[] | null>(null);
  const [fxRate, setFxRate] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [bonusTenantId, setBonusTenantId] = React.useState('');
  const [bonusAmount, setBonusAmount] = React.useState('');
  const [bonusReason, setBonusReason] = React.useState('');
  const [note, setNote] = React.useState<string | null>(null);

  const load = React.useCallback(async (tok: string): Promise<void> => {
    setError(null);
    try {
      const [b, fx] = await Promise.all([
        adminFetch<{ items: BalanceRow[] }>(tok, '/internal/ai-admin/balances'),
        adminFetch<{ usdCzkRate: string | null }>(tok, '/internal/ai-admin/fx-rate'),
      ]);
      setBalances(b.items);
      setFxRate(fx.usdCzkRate);
      setActiveToken(tok);
    } catch (e) {
      setError(errorMessage(e));
      setActiveToken(null);
    }
  }, []);

  async function onBonus(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (activeToken === null) return;
    setNote(null);
    try {
      await adminFetch(activeToken, '/internal/ai-admin/bonus', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantId: bonusTenantId, amountCzk: bonusAmount, reason: bonusReason, createdBy: 'superadmin-ui' }),
      });
      setNote(t2('webAdmin.aiSuperadmin.bonusSaved'));
      await load(activeToken);
    } catch (e) {
      setNote(t2('webAdmin.aiSuperadmin.bonusError', { error: errorMessage(e) }));
    }
  }

  async function onFxRate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (activeToken === null || fxRate === null) return;
    try {
      await adminFetch(activeToken, '/internal/ai-admin/fx-rate', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ rate: fxRate, updatedBy: 'superadmin-ui' }),
      });
      setNote(t2('webAdmin.aiSuperadmin.fxRateSaved'));
    } catch (e) {
      setNote(t2('webAdmin.aiSuperadmin.fxRateError', { error: errorMessage(e) }));
    }
  }

  if (activeToken === null) {
    return (
      <div className="p-6" data-testid="ai-superadmin-login">
        <h1 className="mb-2 text-xl font-bold">{t2('webAdmin.aiSuperadmin.title')}</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void load(token);
          }}
          className="flex max-w-md gap-2"
        >
          <input
            type="password"
            className="flex-1 rounded border px-2 py-1 text-sm"
            placeholder={t2('webAdmin.aiSuperadmin.tokenLabel')}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="ai-superadmin-token"
          />
          <button type="submit" className="rh-a2-link text-sm" data-testid="ai-superadmin-login-submit">
            {t2('webAdmin.aiSuperadmin.tokenSubmit')}
          </button>
        </form>
        {error !== null ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="ai-superadmin-page">
      <h1 className="mb-4 text-xl font-bold">{t2('webAdmin.aiSuperadmin.title')}</h1>

      <h2 className="mb-1 text-sm font-semibold">{t2('webAdmin.aiSuperadmin.balancesTitle')}</h2>
      <table className="mb-4 w-full text-sm" data-testid="ai-superadmin-balances">
        <thead>
          <tr className="text-left text-xs text-gray-600">
            <th>{t2('webAdmin.aiSuperadmin.balancesTenant')}</th>
            <th>{t2('webAdmin.aiSuperadmin.balancesAmount')}</th>
          </tr>
        </thead>
        <tbody>
          {(balances ?? []).map((row) => (
            <tr key={row.tenantId}>
              <td>{row.tenantName}</td>
              <td>{row.balanceCzk}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="mb-1 text-sm font-semibold">{t2('webAdmin.aiSuperadmin.bonusTitle')}</h2>
      <form onSubmit={(e) => void onBonus(e)} className="mb-4 flex flex-wrap gap-2">
        <select
          className="rounded border px-2 py-1 text-sm"
          value={bonusTenantId}
          onChange={(e) => setBonusTenantId(e.target.value)}
          data-testid="ai-superadmin-bonus-tenant"
          required
        >
          <option value="" disabled>
            {t2('webAdmin.aiSuperadmin.balancesTenant')}
          </option>
          {(balances ?? []).map((row) => (
            <option key={row.tenantId} value={row.tenantId}>
              {row.tenantName}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder={t2('webAdmin.aiSuperadmin.bonusAmountLabel')}
          className="w-28 rounded border px-2 py-1 text-sm"
          value={bonusAmount}
          onChange={(e) => setBonusAmount(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder={t2('webAdmin.aiSuperadmin.bonusReasonLabel')}
          className="flex-1 rounded border px-2 py-1 text-sm"
          value={bonusReason}
          onChange={(e) => setBonusReason(e.target.value)}
          required
          minLength={5}
        />
        <button type="submit" className="rh-a2-link text-sm" data-testid="ai-superadmin-bonus-submit">
          {t2('webAdmin.aiSuperadmin.bonusSubmit')}
        </button>
      </form>

      <h2 className="mb-1 text-sm font-semibold">{t2('webAdmin.aiSuperadmin.fxRateTitle')}</h2>
      <form onSubmit={(e) => void onFxRate(e)} className="flex items-end gap-2">
        <input
          type="text"
          className="w-28 rounded border px-2 py-1 text-sm"
          value={fxRate ?? ''}
          onChange={(e) => setFxRate(e.target.value)}
          data-testid="ai-superadmin-fx-rate"
        />
        <button type="submit" className="rh-a2-link text-sm">
          {t2('webAdmin.aiSuperadmin.fxRateSubmit')}
        </button>
      </form>

      {note !== null ? <p className="mt-2 text-sm">{note}</p> : null}
    </div>
  );
}
